import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './BugReports.module.css';
import GlobalHeader from '../GlobalHeader';
import {
  getBugReports,
  resolveBugReport,
  deleteBugReport,
  SavedBugReport,
  BugReportStatus,
} from '../../services/firebase/bugReportService';

type FilterOption = 'all' | BugReportStatus;

function BugReports() {
  const navigate = useNavigate();

  const [reports, setReports] = useState<SavedBugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>('open');

  useEffect(() => {
    fetchReports();
  }, [filter]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const statusFilter = filter === 'all' ? undefined : filter;
      const data = await getBugReports(statusFilter);
      setReports(data);
    } catch (err) {
      setError('Failed to load bug reports.');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await resolveBugReport(id);
      await fetchReports();
    } catch {
      setError('Failed to resolve report.');
    }
  };

  const handleDelete = async (report: SavedBugReport) => {
    if (!window.confirm(`Delete bug report "${report.title}"? This cannot be undone.`)) return;
    try {
      await deleteBugReport(report);
      await fetchReports();
    } catch {
      setError('Failed to delete report.');
    }
  };

  const formatDate = (timestamp: any) => {
    try {
      const date = timestamp?.toDate?.() || new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown date';
    }
  };

  const truncateAgent = (ua: string) => {
    if (!ua) return '';
    if (ua.length <= 60) return ua;
    return ua.substring(0, 60) + '...';
  };

  return (
    <div className={styles.container}>
      <GlobalHeader
        showBackButton={true}
        backUrl="/game"
        showPlayerOptions={true}
      />

      <div className={styles.content}>
        <h1 className={styles.pageTitle}>Bug Reports</h1>

        <div className={styles.filterBar}>
          {(['open', 'resolved', 'all'] as FilterOption[]).map((opt) => (
            <button
              key={opt}
              className={`${styles.filterButton} ${filter === opt ? styles.activeFilter : ''}`}
              onClick={() => setFilter(opt)}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>

        {!loading && (
          <div className={styles.reportCount}>
            {reports.length} report{reports.length !== 1 ? 's' : ''}
          </div>
        )}

        {loading ? (
          <div className={styles.loading}>Loading bug reports...</div>
        ) : error ? (
          <div className={styles.errorMsg}>{error}</div>
        ) : reports.length === 0 ? (
          <div className={styles.emptyState}>
            No {filter !== 'all' ? filter : ''} bug reports found.
          </div>
        ) : (
          <div className={styles.reportList}>
            {reports.map((report) => (
              <div key={report.id} className={styles.reportCard}>
                <div className={styles.reportHeader}>
                  <span className={styles.reportTitle}>{report.title}</span>
                  <span className={`${styles.statusBadge} ${report.status === 'open' ? styles.statusOpen : styles.statusResolved}`}>
                    {report.status}
                  </span>
                  {report.page && report.page !== 'Not specified' && (
                    <span className={styles.pageBadge}>{report.page}</span>
                  )}
                </div>

                <div className={styles.reportBody}>
                  <div className={styles.reportDescription}>{report.description}</div>

                  {report.stepsToReproduce && (
                    <div className={styles.stepsSection}>
                      <div className={styles.stepsLabel}>Steps to Reproduce</div>
                      <div className={styles.stepsText}>{report.stepsToReproduce}</div>
                    </div>
                  )}
                </div>

                <div className={styles.reportMeta}>
                  <span>By: {report.reportedByUsername}</span>
                  <span>{formatDate(report.createdAt)}</span>
                  {report.currentUrl && <span>URL: {report.currentUrl}</span>}
                  {report.userAgent && <span title={report.userAgent}>{truncateAgent(report.userAgent)}</span>}
                </div>

                <div className={styles.reportActions}>
                  {report.status === 'open' && (
                    <button
                      className={styles.resolveButton}
                      onClick={() => handleResolve(report.id)}
                    >
                      Mark Resolved
                    </button>
                  )}
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDelete(report)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

export default BugReports;
